/**
 * ChatBlockRenderers — 聊天气泡内的"拟物化渲染"拦截组件
 *
 * 严格按照 implementation_plan.md 第三阶段实现：
 *
 * 1. SpreadsheetBlock: 当 AI 输出 ```csv 或 ```sheet 代码块时，
 *    使用 Papaparse 解析并用 AG Grid 显示，最大 400px 滚动。
 *
 * 2. A4PaperBlock: 当 AI 输出 ```document 代码块时，
 *    渲染成白色背景、深刻 box-shadow 的 A4 拟物纸张，
 *    使用 prose 衬线字体排版。
 */

import React, { useMemo } from 'react';
import Papa from 'papaparse';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';

// AG Grid 社区模块注册
ModuleRegistry.registerModules([AllCommunityModule]);

// ──────────────────────────────────────────
// SpreadsheetBlock: CSV/Sheet 拟物化表格
// ──────────────────────────────────────────

interface SpreadsheetBlockProps {
  content: string;
}

export const SpreadsheetBlock: React.FC<SpreadsheetBlockProps> = ({ content }) => {
  const { columnDefs, rowData } = useMemo(() => {
    const parsed = Papa.parse(content.trim(), {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
    });

    if (!parsed.data || parsed.data.length === 0) {
      return { columnDefs: [], rowData: [] };
    }

    const headers = parsed.meta.fields || Object.keys(parsed.data[0] as object);
    return {
      columnDefs: headers.map(field => ({
        field,
        headerName: field,
        sortable: true,
        resizable: true,
        minWidth: 100,
      })),
      rowData: parsed.data as Record<string, unknown>[],
    };
  }, [content]);

  if (rowData.length === 0) {
    return <pre className="text-sm text-gray-500 p-3">{content}</pre>;
  }

  return (
    <div className="my-3 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
      {/* AG Grid 表格 */}
      <div style={{ height: Math.min(400, 42 + rowData.length * 36), width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          domLayout="normal"
          suppressHorizontalScroll={false}
          defaultColDef={{
            flex: 1,
            minWidth: 80,
            filter: true,
          }}
        />
      </div>
      {/* 底部提示条 */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
        <span>{rowData.length} rows × {columnDefs.length} columns</span>
        <span className="text-gray-400">📊 Tabular Data</span>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────
// A4PaperBlock: 法律文书 A4 拟物纸张
// ──────────────────────────────────────────

interface A4PaperBlockProps {
  content: string;
}

export const A4PaperBlock: React.FC<A4PaperBlockProps> = ({ content }) => {
  return (
    <div className="my-4 flex justify-center">
      <div
        className="bg-white rounded shadow-[0_2px_20px_rgba(0,0,0,0.12)] border border-gray-100"
        style={{
          maxWidth: '680px',
          width: '100%',
          padding: '48px 56px',
          fontFamily: '"Noto Serif SC", "Georgia", "Times New Roman", serif',
          fontSize: '14px',
          lineHeight: '1.8',
          color: '#1a1a1a',
        }}
      >
        {/* 按段落渲染 */}
        {content.split('\n').map((para, i) => {
          const trimmed = para.trim();
          if (!trimmed) return <div key={i} style={{ height: '12px' }} />;
          // 标题检测
          if (trimmed.startsWith('# ')) {
            return (
              <h1 key={i} style={{ fontSize: '20px', fontWeight: 700, marginBottom: '16px', textAlign: 'center' }}>
                {trimmed.slice(2)}
              </h1>
            );
          }
          if (trimmed.startsWith('## ')) {
            return (
              <h2 key={i} style={{ fontSize: '17px', fontWeight: 600, marginBottom: '12px', marginTop: '20px' }}>
                {trimmed.slice(3)}
              </h2>
            );
          }
          if (trimmed.startsWith('### ')) {
            return (
              <h3 key={i} style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px', marginTop: '16px' }}>
                {trimmed.slice(4)}
              </h3>
            );
          }
          return (
            <p key={i} style={{ marginBottom: '8px', textIndent: '2em', textAlign: 'justify' }}>
              {trimmed}
            </p>
          );
        })}
      </div>
    </div>
  );
};
